import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { GameAnswerObject } from 'src/app/data-models/game-answer-object';
import { CookieService } from 'src/app/services/cookie.service';
import { QuestionService, QuestionData, Difficulty } from 'src/app/services/question.service';
import { Language, LanguageService } from 'src/app/services/language-service.service';
import { PopupMenuComponent } from 'src/app/subcomponents/popup-menu/popup-menu.component';
import { TimerService } from 'src/app/services/timer.service';
import { Subscription } from 'rxjs';
import { PointsAndLivesService } from 'src/app/services/points-and-lives.service';
import { AccessibilityService } from 'src/app/services/accessibility.service';

@Component({
  selector: 'app-game',
  templateUrl: './game.component.html',
  styleUrls: ['./game.component.css']
})
export class GameComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('tutorial', {read: PopupMenuComponent, static: true}) tutorialPopup: PopupMenuComponent;
  @ViewChild('pausePopup', {read: PopupMenuComponent, static: true}) pausePopup: PopupMenuComponent;

  public difficulty = Difficulty.Easy;
  public learnLanguage = Language.ENG;
  public uiLanguage = Language.FIN;

  public clockTime = {minutes: '0',  seconds: '0'};

  public score = 123450;
  public streak = 888;
  public lives = 3;

  public answerChoices: GameAnswerObject[] = [];
  public correctAnswer = 'banana';

  public answerGiven = false;
  public answer: GameAnswerObject;

  public currentQuestion: QuestionData;

  public options: GameAnswerObject[];

  public question: string;

  public visualAccessibilityOn = false;
  public colorblindModeOn = false;
  public noStressMode = false;

  private timeOutSubscriber: Subscription;
  private clockSubscriber: Subscription;
  public keyboardControlOn = false;
  private keyboardSubscriber: Subscription;

  private menuOpen = false;
  private tutorialOpen = false;

  constructor(private accessibilityHandler: AccessibilityService,
              public router: Router,
              private questionService: QuestionService,
              private languageService: LanguageService,
              private cookieService: CookieService,
              private timerService: TimerService,
              private pointsAndLivesService: PointsAndLivesService) { }

  ngOnInit(): void {

    this.pointsAndLivesService.CheckRelevantCookieData();
    this.accessibilityHandler.CheckRelevantCookieData();

    this.visualAccessibilityOn = this.accessibilityHandler.highContrastAccessibilityOn;
    this.colorblindModeOn = this.accessibilityHandler.colorBlindAccessibilityOn;
    this.noStressMode = this.accessibilityHandler.stressFreeAccessibilityOn;

    // NOTE: Likeliest possibilities for below evaluating to true:
    //        - player pressed return on browser
    //        - player refreshed page when the game took a second to show the correct answer
    if (this.pointsAndLivesService.currentLives === 0) {

      if (this.pointsAndLivesService.endScoreShown) {
        this.router.navigate(['/start-game']);
      }
      else {
        this.router.navigate(['/end-game']);
      }

      return;
    }

    this.languageService.CheckRelevantCookieData();
    this.questionService.CheckRelevantCookieData();
    this.timerService.CheckRelevantCookieData();

    this.difficulty = this.questionService.difficulty;
    this.learnLanguage = this.languageService.learnLanguage;
    this.uiLanguage = this.languageService.uiLanguage;

    switch (this.difficulty) {
      case Difficulty.Easy: {
        this.question = 'easy_question';
        break;
      }
      case Difficulty.Medium: {
        this.question = 'intermediate_question';
        break;
      }
      case Difficulty.Hard: {
        this.question = 'hard_question';
        break;
      }
    }

    this.clockSubscriber = this.timerService.currentClockTime.subscribe(clockTime => {
      this.clockTime = clockTime;
    });

    this.keyboardControlOn = this.accessibilityHandler.keyboardAccessibilityOn;

    this.keyboardSubscriber = this.accessibilityHandler.keyEventObs.subscribe(buttonPress => {

      if (!this.accessibilityHandler.keyboardAccessibilityOn) {
        return;
      }

      switch (buttonPress.key) {
        case '1': {
          this.GiveAnswer(this.answerChoices[0]);
          break;
        }
        case '2': {
          this.GiveAnswer(this.answerChoices[1]);
          break;
        }
        case '3': {
          this.GiveAnswer(this.answerChoices[2]);
          break;
        }
        case '4': {
          this.GiveAnswer(this.answerChoices[3]);
          break;
        }
        case 'p': {
          if (this.menuOpen){
            this.CloseMenu();
          }
          else {
            this.OpenMenu();
          }
          break;
        }
        case 'P': {
          if (this.menuOpen){
            this.CloseMenu();
          }
          else {
            this.OpenMenu();
          }
          break;
        }
        case 'Enter': {
          if (this.menuOpen){
            this.EndGame(true);
          }
          else if (this.tutorialOpen) {
            this.CloseTutorial();
          }
          break;
        }
      }

    });

    this.score = this.pointsAndLivesService.currentPoints;
    this.streak = this.pointsAndLivesService.currentStreak;
    this.lives = this.pointsAndLivesService.currentLives;

    this.NextQuestion();
    this.StartTimer();

  }

  ngAfterViewInit(): void {

    if (!this.questionService.tutorialSeen) {
      if (!this.noStressMode) { this.timerService.StopTheTimer(true); }
      this.tutorialPopup.openPopupMenu();
      this.tutorialOpen = true;
    }
  }

  ngOnDestroy(): void {
    this.keyboardSubscriber.unsubscribe();
  }

  delay(ms: number): Promise<any> {
    return new Promise( resolve => setTimeout(resolve, ms) );
  }


  public async GiveAnswer(answer: GameAnswerObject): Promise<void> {
    if (this.answerGiven) { return; }
    this.answerGiven = true;
    this.answer = answer;
    const isCorrect = answer !== null && this.currentQuestion.correctAnswer.Equals(answer);
    const continueGame = this.pointsAndLivesService.HandleAnswer(isCorrect);
    this.lives = this.pointsAndLivesService.currentLives;
    if (!this.noStressMode) { this.timerService.StopTheTimer(); }
    await this.delay(3000);
    if (continueGame) {
      this.score = this.pointsAndLivesService.currentPoints;
      this.streak = this.pointsAndLivesService.currentStreak;
      this.IncrementQuestionCounters();
      this.NextQuestion();
      this.StartTimer();
    }
    else {
      this.router.navigate(['/end-game']);
    }
  }

  public CloseTutorial(): void {
    this.questionService.tutorialSeen = true;
    this.cookieService.SetCookieValue('tutorialSeen', 'true');
    this.StartTimer();
    this.tutorialPopup.closePopupMenu();
    this.tutorialOpen = false;
  }

  public OpenMenu(): void {
    this.menuOpen = true;
    this.pausePopup.openPopupMenu();
    if (!this.noStressMode) { this.timerService.StopTheTimer(true); }
  }

  public CloseMenu(): void {
    this.menuOpen = false;
    this.pausePopup.closePopupMenu();
    this.StartTimer();
  }


  public EndGame(endedFromPauseMenu: boolean = false): void {
    this.pausePopup.closePopupMenu();
    if (!this.noStressMode) { this.timerService.StopTheTimer(); }
    this.router.navigate(['/']);
  }


  private NextQuestion(): void {
    this.currentQuestion = this.questionService.GetNextQuestion();
    this.answerChoices = [];
    for (const answer of this.currentQuestion.wrongAnswers){
      this.answerChoices.push(new GameAnswerObject(answer));
    }

    if (this.difficulty === Difficulty.Hard && this.visualAccessibilityOn) {
      this.answerChoices.pop();
    }

    const insertIndex = this.questionService.RandomIndex(this.answerChoices, true);

    if (insertIndex < this.answerChoices.length) {
      this.answerChoices.splice(insertIndex, 0, this.currentQuestion.correctAnswer);
    }
    else{
      this.answerChoices.push(this.currentQuestion.correctAnswer);
    }

    this.answerGiven = false;
    this.answer = null;
  }


  private StartTimer(): void {

    if (this.noStressMode) { return; }

    this.timerService.StartTimer();

    if (!this.timeOutSubscriber) {
      this.timeOutSubscriber = this.timerService.timeOutNotifier.subscribe(timedOutBoolean => {
        this.HandleTimeout(timedOutBoolean);
      });
    }

  }

  private IncrementQuestionCounters(): void {
    this.timerService.IncrementQuestionCount();
    this.questionService.IncrementQuestionCount();
  }

  private HandleTimeout(timedOut: boolean): void {

    if (!timedOut) { return; }

    this.GiveAnswer(null);

  }

}
